import { Pencil } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface MatterSectionCardProps {
  /** Card title displayed in the header */
  title?: string;
  /** Optional icon to display before the title */
  icon?: React.ReactNode;
  /** Content to render in the card body */
  children: React.ReactNode;
  /** Show edit button in header */
  showEditButton?: boolean;
  /** Callback when edit button is clicked */
  onEdit?: () => void;
  /** Whether the card is in editing mode */
  isEditing?: boolean;
  /** Custom actions to render in the header (right side) */
  headerActions?: React.ReactNode;
  /** Additional className for the card container */
  className?: string;
  /** Additional className for the card body */
  bodyClassName?: string;
  /** Whether to show loading skeleton */
  isLoading?: boolean;
  /** Hide the header entirely */
  hideHeader?: boolean;
}

/**
 * A unified card component for all sections in the Matter page.
 * Provides consistent styling across Summary, People, Accident, Insurance tabs.
 *
 * @example
 * // Basic usage with title
 * <MatterSectionCard title="Case Overview">
 *   <p>Card content here</p>
 * </MatterSectionCard>
 *
 * @example
 * // With edit button
 * <MatterSectionCard title="Client Information" showEditButton onEdit={() => setEditing(true)}>
 *   <ClientForm />
 * </MatterSectionCard>
 *
 * @example
 * // With custom header actions
 * <MatterSectionCard title="Related Cases" headerActions={<Button size="sm">Link Cases</Button>}>
 *   <CasesList />
 * </MatterSectionCard>
 */
export const MatterSectionCard: React.FC<MatterSectionCardProps> = ({
  title,
  icon,
  children,
  showEditButton = false,
  onEdit,
  isEditing = false,
  headerActions,
  className,
  bodyClassName,
  isLoading = false,
  hideHeader = false,
}) => {
  if (isLoading) {
    return (
      <div
        className={cn(
          'bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden',
          className,
        )}
      >
        {!hideHeader && (
          <div className="px-4 py-3 border-b border-zinc-100">
            <Skeleton className="h-4 w-32" />
          </div>
        )}
        <div className={cn('p-4', bodyClassName)}>
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  const showHeader =
    !hideHeader && (title || icon || showEditButton || headerActions);

  return (
    <div
      className={cn(
        'bg-white rounded-lg border-zinc-300 border overflow-hidden',
        className,
      )}
    >
      {showHeader && (
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon && <span className="text-zinc-400">{icon}</span>}
            {title && (
              <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
            )}
          </div>
          <div className="flex items-center gap-2">
            {headerActions}
            {showEditButton && !isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="h-7 px-2 text-xs text-zinc-500 hover:text-zinc-700"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
      <div className={cn('p-4', bodyClassName)}>{children}</div>
    </div>
  );
};

/**
 * A simpler variant without header, just the card styling.
 * Useful for wrapping existing components that have their own headers.
 */
export const MatterSectionCardSimple: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden',
        className,
      )}
    >
      {children}
    </div>
  );
};
